import { getRouteApi } from '@tanstack/react-router';
import {
  addHours,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { useCallback, useMemo } from 'react';
import { z } from 'zod';

import { useOneRosterAuth } from '@/hooks/useOneRosterAuth';

import { parseIsoDateToLocal } from '../utils/dateUtils';

const routeApi = getRouteApi('/_app/app/learning-metrics');

export const middleOfDay = (date: Date) => {
  return addHours(startOfDay(date), 12);
};

export const activityViewSearchParams = z.object({
  view: z.enum(['daily', 'weekly', 'monthly']).optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  studentIds: z.string().optional(),
  groupByApp: z.boolean().optional(),
  showAllMetrics: z.boolean().optional(),
  showAllSubjects: z.boolean().optional().default(true),
});

export type ActivityViewSearchParams = z.infer<typeof activityViewSearchParams>;
export type ActivityView = NonNullable<ActivityViewSearchParams['view']>;

export type ActivityViewState = {
  view: ActivityView;
  startDate: Date;
  endDate: Date;
  studentId: string;
  selectedStudentsIds: string[];
};

export function useActivityViewSearch() {
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  const auth = useOneRosterAuth();
  const user = auth.user!;
  const { isParent, isAide, isAdmin } = auth;

  const view = search.view ?? 'daily';
  const skipDefaultStudentId = isAdmin || isAide;

  let defaultStudentId: string = skipDefaultStudentId ? '' : user.sourcedId;
  if (isParent) {
    defaultStudentId = auth.children[0]?.sourcedId ?? '';
  }

  const studentIds = useMemo(() => {
    if (search.studentIds) {
      return search.studentIds.split(',').filter((id) => id.trim() !== '');
    }

    return [defaultStudentId];
  }, [search.studentIds, defaultStudentId]);

  const startDate = useMemo(() => {
    if (search.startDate) {
      const date = parseIsoDateToLocal(search.startDate);
      return middleOfDay(date);
    }

    const today = new Date();

    if (view === 'daily') {
      return middleOfDay(today);
    } else if (view === 'weekly') {
      return middleOfDay(startOfWeek(today, { weekStartsOn: 0 })); // 0 = Sunday
    } else if (view === 'monthly') {
      return middleOfDay(startOfMonth(today));
    }

    return today;
  }, [search.startDate, view]);

  const endDate = useMemo(() => {
    if (search.endDate) {
      const endDate = parseIsoDateToLocal(search.endDate);
      return middleOfDay(endDate);
    }

    if (view === 'daily') {
      return middleOfDay(endOfDay(startDate));
    } else if (view === 'weekly') {
      return middleOfDay(endOfWeek(startDate, { weekStartsOn: 0 })); // 0 = Sunday
    } else if (view === 'monthly') {
      return middleOfDay(endOfMonth(startDate));
    }

    return new Date();
  }, [search.endDate, view, startDate]);

  const setView = useCallback(
    (
      newView: ActivityViewSearchParams['view'],
      newDateRange?: { startDate: Date; endDate: Date }
    ) => {
      const today = new Date();
      let newStartDate = startDate;
      let newEndDate = endDate;

      if (newDateRange) {
        newStartDate = newDateRange.startDate;
        newEndDate = newDateRange.endDate;
      } else {
        switch (newView) {
          case 'daily':
            newStartDate = middleOfDay(today);
            newEndDate = newStartDate;
            break;
          case 'weekly':
            newStartDate = middleOfDay(startOfWeek(today, { weekStartsOn: 0 }));
            newEndDate = middleOfDay(endOfWeek(newStartDate));
            break;
          case 'monthly':
            newStartDate = middleOfDay(startOfMonth(today));
            newEndDate = middleOfDay(endOfMonth(newStartDate));
            break;
        }
      }

      navigate({
        search: {
          ...search,
          view: newView,
          startDate: format(newStartDate, 'yyyy-MM-dd'),
          endDate: format(newEndDate, 'yyyy-MM-dd'),
        },
      });
    },
    [search, navigate, startDate, endDate]
  );

  const setDateRange = useCallback(
    (newStartDate: Date, newEndDate: Date) => {
      navigate({
        search: {
          ...search,
          startDate: format(middleOfDay(newStartDate), 'yyyy-MM-dd'),
          endDate: format(middleOfDay(newEndDate), 'yyyy-MM-dd'),
        },
      });
    },
    [search, navigate]
  );

  const setStudentIds = useCallback(
    (newStudentIds: string[] | null) => {
      const studentIds =
        newStudentIds && newStudentIds.length > 0
          ? newStudentIds.join(',')
          : undefined;

      navigate({
        search: {
          ...search,
          studentIds,
        },
      });
    },
    [search, navigate]
  );

  const setGroupByApp = useCallback(
    (groupByApp: boolean) => {
      navigate({ search: { ...search, groupByApp } });
    },
    [search, navigate]
  );

  const setShowAllMetrics = useCallback(
    (showAllMetrics: boolean) => {
      navigate({ search: { ...search, showAllMetrics } });
    },
    [search, navigate]
  );

  const setShowAllSubjects = useCallback(
    (showAllSubjects: boolean) => {
      navigate({ search: { ...search, showAllSubjects } });
    },
    [search, navigate]
  );

  return {
    view,
    studentIds: studentIds.filter(Boolean),
    startDate,
    endDate,
    groupByApp: search.groupByApp ?? false,
    showAllMetrics: search.showAllMetrics ?? false,
    setView,
    setDateRange,
    setStudentIds,
    setGroupByApp,
    setShowAllMetrics,
    showAllSubjects: search.showAllSubjects ?? false,
    setShowAllSubjects,
  };
}
